import { useState } from "react";
import { Link } from "react-router-dom";
import SEO, { websiteSchema, organizationSchema, faqSchema } from "@/components/SEO";
import { trackNewsletterSignup } from "@/lib/analytics";
import AnimatedFlowDemo from "@/components/AnimatedFlowDemo";
import InspirationSection from "@/components/InspirationSection";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  Sparkles, 
  FileText, 
  Gift,
  ChevronRight,
  Star,
  Users,
  Heart,
  Shield,
  Edit3,
  Eye,
  RefreshCw,
  Check,
  Cake,
  Clock,
  Target,
  Pencil,
  Mail,
  Loader2
} from "lucide-react";
import Letterhead, { 
  EXAMPLE_WEDDING_FORM, 
  EXAMPLE_WEDDING_SPEECH,
  EXAMPLE_CONFIRMATION_FORM,
  EXAMPLE_CONFIRMATION_SPEECH,
  EXAMPLE_BIRTHDAY_FORM,
  EXAMPLE_BIRTHDAY_SPEECH
} from "@/components/Letterhead";
import ExampleCard from "@/components/ExampleCard";

const API = process.env.REACT_APP_BACKEND_URL;

// FAQs visible on landing page — kept in sync with the Accordion section below.
// Used to emit a FAQPage JSON-LD schema (Google requires visible content).
const LANDING_FAQS = [
  {
    q: "Hvordan virker det?",
    a: "Du udfylder en kort formular med information om personen og anledningen — navne, jeres relation, sjove anekdoter og hvad du gerne vil sige. Vores AI skriver en personlig tale baseret på dine input. Du kan se et preview med det samme, og efter betaling kan du redigere og tilpasse talen frit.",
  },
  {
    q: "Er talen virkelig personlig?",
    a: "Ja! Talen bygger 100% på de historier og detaljer du deler i formularen. Jo mere du skriver, jo bedre bliver talen. Vores AI opfinder aldrig historier — den bruger kun det du giver den.",
  },
  {
    q: "Kan jeg redigere talen bagefter?",
    a: "Absolut. Efter køb får du adgang til en editor hvor du kan ændre formuleringer, tilføje sætninger og gøre talen helt til din egen. Du kan downloade den færdige tale som PDF og ren tekst.",
  },
  {
    q: "Hvad hvis jeg ikke er tilfreds?",
    a: "Du har 100% tilfredshedsgaranti. Inden for 24 timer efter køb kan du få genereret en helt ny version gratis. Du kan også prøve ubegrænset med forskellige toner inden du betaler.",
  },
  {
    q: "Er det AI der skriver talen?",
    a: "Ja, vi bruger AI-teknologi der er specialiseret i danske festtaler. Men tænk på det som et samarbejde: Du leverer historierne og det personlige — vores AI pakker det ind i en velskrevet tale med den rigtige opbygning, timing og tone. Du tilpasser den til den er perfekt.",
  },
  {
    q: "Hvor lang tid tager det?",
    a: "Selve genereringen tager under 30 sekunder. Med formularen medregnet er de fleste færdige på 2-5 minutter. Redigering bagefter er op til dig.",
  },
  {
    q: "Hvad er forskellen på kort, medium og lang tale?",
    a: "Kort (~2 min taletid, 350-450 ord) er perfekt hvis du vil sige noget kort og rammende. Medium (~4 min, 700-900 ord) giver plads til 2-3 anekdoter og er vores mest populære. Lang (~6 min, 1000-1300 ord) er til den store lejlighed hvor du virkelig vil udfolde historien.",
  },
  {
    q: "Kan jeg se et eksempel inden jeg starter?",
    a: "Ja! Længere oppe på denne side kan du se tre komplette eksempler — en bryllupstale, en konfirmationstale og en fødselsdagstale — med den formular der blev udfyldt og den færdige tale. Du kan også se et preview af din egen tale inden du betaler.",
  },
  {
    q: "Bliver mine personlige oplysninger gemt?",
    a: "Vi tager dit privatliv alvorligt. Dine formulardata bruges kun til at generere din tale og slettes automatisk efter 30 dage. Vi sælger aldrig dine data, og de bruges ikke til at træne AI-modeller.",
  },
  {
    q: "Kan jeg bruge talen til en anledning I ikke har på listen?",
    a: "Ja! Udover bryllup, konfirmation og rund fødselsdag kan du vælge 'Anden anledning' og beskrive festen. Vores AI tilpasser talen til alt fra studenterfest til sølvbryllup til afskedsfest.",
  },
  {
    q: "Hvorfor ikke bare bruge ChatGPT?",
    a: "Det kan du — men ChatGPT kender ikke danske taletraditioner, giver dig ingen talenoter med pauser og leveringstips, og det tager typisk 30+ minutter at prompte sig frem til et brugbart resultat. MinFesttale er bygget specifikt til danske festtaler: Du svarer på simple spørgsmål, får 2 unikke udkast med talenoter på 30 sekunder, og kan redigere og downloade som flot PDF. Det er forskellen på at bygge selv og at få det leveret.",
  },
  {
    q: "Hvordan kontakter jeg kundeservice?",
    a: "Skriv til hej@minfesttale.dk — vi svarer typisk inden for 24 timer (ofte hurtigere). Vi tilbyder kun kundeservice via e-mail, ikke telefon, så vi kan holde priserne lave og bruge tiden på at hjælpe dig med din tale i stedet.",
  },
];


const NewsletterForm = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/newsletter/subscribe`, { email });
      trackNewsletterSignup();
      setSubmitted(true);
      toast.success(res.data.message);
    } catch {
      toast.error("Noget gik galt. Prøv igen.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex items-center justify-center gap-2 text-white" data-testid="newsletter-success">
        <Check className="h-5 w-5 text-secondary" />
        <span className="font-medium">Tak for din tilmelding!</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="newsletter-form">
      <Input
        type="email"
        placeholder="Din email-adresse"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        className="bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-full px-5 h-12 focus:border-secondary"
        data-testid="newsletter-email-input"
      />
      <Button 
        type="submit" 
        disabled={loading}
        className="bg-secondary hover:bg-secondary/90 text-primary rounded-full px-6 h-12 font-medium whitespace-nowrap"
        data-testid="newsletter-submit-btn"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Tilmeld"}
      </Button>
    </form>
  );
};

const LandingPage = () => {
  const [selectedExample, setSelectedExample] = useState("wedding");
  const [expandedExample, setExpandedExample] = useState(null);

  const examples = [
    {
      id: "wedding",
      label: "Bryllup",
      shortLabel: "Bryllup",
      icon: Heart,
      color: "bg-rose-500",
      formData: EXAMPLE_WEDDING_FORM,
      speech: EXAMPLE_WEDDING_SPEECH,
      title: "Bryllupstale",
      subtitle: "Til Anne & Thomas · Fra Mikkel"
    },
    {
      id: "confirmation",
      label: "Konfirmation",
      shortLabel: "Konfirm.",
      icon: Sparkles,
      color: "bg-amber-500",
      formData: EXAMPLE_CONFIRMATION_FORM,
      speech: EXAMPLE_CONFIRMATION_SPEECH,
      title: "Konfirmationstale",
      subtitle: "Til Frederik · Fra Peter (far)"
    },
    {
      id: "birthday",
      label: "60 års fødselsdag",
      shortLabel: "Fødselsdag",
      icon: Cake,
      color: "bg-emerald-500",
      formData: EXAMPLE_BIRTHDAY_FORM,
      speech: EXAMPLE_BIRTHDAY_SPEECH,
      title: "Fødselsdagstale",
      subtitle: "Til Hanne · Fra Lars (ægtefælle)"
    }
  ];

  const currentExample = examples.find(e => e.id === selectedExample);

  return (
    <div className="paper-texture">
      <SEO
        title="MinFesttale.dk — Personlige festtaler skrevet med AI"
        description="Få en personlig festtale til bryllup, konfirmation eller rund fødselsdag på få minutter. AI-skrevne taler baseret på dine egne anekdoter. 2 udkast, ubegrænset redigering, PDF-download."
        path="/"
        schema={[websiteSchema(), organizationSchema(), faqSchema(LANDING_FAQS)]}
      />
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/8c033565-2945-4a08-9475-67d051518b07/images/88f07a9d44351f45c87e558d4d0741ae6eebb550327ed7a5dde16e2454fc12eb.png')`
          }}
        />
        <div className="absolute inset-0 bg-primary/50" />
        
        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center text-white">
          <span className="inline-block font-accent text-2xl md:text-3xl text-secondary mb-4">
            Gør din tale uforglemmelig
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
            Skriv en personlig festtale
            <br />
            <span className="text-secondary">på 2 minutter</span>
          </h1>
          <p className="text-base md:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Udfyld en kort formular med dine personlige historier. 
            Vores AI skriver en tale du kan være stolt af. 
            Tilpas den selv til den er perfekt.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/tale">
              <Button 
                size="lg" 
                className="bg-secondary hover:bg-secondary/90 text-primary rounded-full px-8 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all"
                data-testid="hero-cta-tale"
              >
                <FileText className="mr-2 h-5 w-5" />
                Start din tale nu
              </Button>
            </Link>
          </div>
          
          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-3 md:gap-4 mt-10 md:mt-12">
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 md:px-4 py-2 rounded-full text-sm">
              <Check className="h-4 w-4 text-secondary" />
              <span>Se preview før du betaler</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 md:px-4 py-2 rounded-full text-sm">
              <Check className="h-4 w-4 text-secondary" />
              <span>Tilpas selv teksten bagefter</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 md:px-4 py-2 rounded-full text-sm">
              <Check className="h-4 w-4 text-secondary" />
              <span>100% tilfredshedsgaranti</span>
            </div>
          </div>

          {/* Featured testimonial — highlighted social proof */}
          <div className="mt-12 md:mt-16 max-w-3xl mx-auto" data-testid="hero-featured-testimonial">
            <div className="relative bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-secondary/30 p-6 md:p-10">
              {/* Decorative quote mark */}
              <div className="absolute -top-4 left-6 md:left-10 bg-secondary text-primary rounded-full w-10 h-10 md:w-12 md:h-12 flex items-center justify-center font-serif text-3xl md:text-4xl shadow-lg">
                &ldquo;
              </div>
              <div className="flex justify-center mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 md:h-6 md:w-6 fill-secondary text-secondary" />
                ))}
              </div>
              <p className="font-heading text-lg md:text-2xl text-primary text-center leading-relaxed italic mb-5 md:mb-6">
                Min kone fyldte 60 og jeg er IKKE typen der holder taler. Normalt ville jeg bare sige &lsquo;tillykke&rsquo; og sætte mig ned. Talen var personlig, sjov og rørende — mine døtre troede jeg havde brugt uger på den.
              </p>
              <div className="flex items-center justify-center gap-3">
                <div className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0">
                  <Users className="h-5 w-5 md:h-6 md:w-6 text-secondary" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-primary text-sm md:text-base">Per, 62 år</p>
                  <p className="text-xs md:text-sm text-muted-foreground">Hyldesttale til konens 60 års fødselsdag</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Animated flow demo */}
      <AnimatedFlowDemo />

      {/* Inspiration grid — 4 occasions */}
      <InspirationSection />

      {/* Examples Section - Form → Speech */}
      <section className="py-12 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <span className="text-secondary font-medium text-sm uppercase tracking-wider">Se hvad du får</span>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-bold text-primary mt-2">
              Se hvad du får — tre rigtige eksempler
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto text-sm md:text-base">
              Fra udfyldt formular til færdig tale på brevpapir
            </p>
          </div>

          {/* Example Tabs */}
          <Tabs value={selectedExample} onValueChange={setSelectedExample} className="mb-8">
            <TabsList className="grid w-full grid-cols-3 bg-muted/50 p-1 h-auto">
              {examples.map((example) => (
                <TabsTrigger 
                  key={example.id}
                  value={example.id}
                  className="flex items-center gap-2 py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  data-testid={`example-tab-${example.id}`}
                >
                  <example.icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{example.label}</span>
                  <span className="sm:hidden text-xs">{example.shortLabel}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {examples.map((example) => (
              <TabsContent key={example.id} value={example.id} className="mt-6 relative">
                <ExampleCard
                  formData={example.formData}
                  speech={example.speech}
                  title={example.title}
                  subtitle={example.subtitle}
                  occasionIcon={example.icon}
                  occasionColor={example.color}
                  expanded={expandedExample === example.id}
                  onToggle={() => setExpandedExample(expandedExample === example.id ? null : example.id)}
                  testId={`example-card-${example.id}`}
                />
              </TabsContent>
            ))}
          </Tabs>

          {/* CTA under examples */}
          <div className="text-center mt-8 md:mt-12 p-6 md:p-8 bg-muted/30 rounded-2xl">
            <p className="text-lg md:text-xl text-primary font-medium mb-4">
              2 minutter. Fra 149 kr. 100% tilfredshedsgaranti.
            </p>
            <Link to="/tale">
              <Button 
                size="lg" 
                className="bg-secondary hover:bg-secondary/90 text-primary rounded-full px-8 py-6 text-lg"
                data-testid="examples-cta"
              >
                Prøv selv — lav din egen tale nu
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-12 md:py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-12">
            <span className="text-secondary font-medium text-sm uppercase tracking-wider">Så nemt er det</span>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-bold text-primary mt-2">
              Tre simple trin
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                step: "1",
                title: "Udfyld formularen",
                description: "Fortæl os om personen og anledningen. Del dine anekdoter og ønsker.",
                icon: FileText
              },
              {
                step: "2",
                title: "Få 2 udkast",
                description: "Se to unikke versioner af din tale med det samme. Vælg din favorit eller mix det bedste fra begge.",
                icon: Sparkles
              },
              {
                step: "3",
                title: "Betal & rediger",
                description: "Lås op, tilpas teksten selv til den er perfekt, og download som flot PDF.",
                icon: Gift
              }
            ].map((item, index) => (
              <Card 
                key={index} 
                className="relative bg-white hover-lift border-border/40"
                data-testid={`step-card-${index + 1}`}
              >
                <CardContent className="p-6 md:p-8 text-center">
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-secondary text-primary font-bold flex items-center justify-center">
                    {item.step}
                  </div>
                  <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-5 md:mb-6 rounded-full bg-primary/5 flex items-center justify-center">
                    <item.icon className="h-7 w-7 md:h-8 md:w-8 text-primary" />
                  </div>
                  <h3 className="font-heading text-lg md:text-xl font-bold text-primary mb-3">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground text-sm md:text-base">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why MinFesttale Section */}
      <section className="py-12 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-12">
            <span className="text-secondary font-medium text-sm uppercase tracking-wider">Fordelene</span>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-bold text-primary mt-2">
              Hvorfor vælge MinFesttale?
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto text-sm md:text-base">
              Du kan selvfølgelig skrive talen selv. Men her er grunden til at de fleste ikke gør det.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            <Card className="bg-white hover-lift border-border/40" data-testid="why-card-1">
              <CardContent className="p-6 md:p-8 text-center">
                <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-5 md:mb-6 rounded-full bg-secondary/10 flex items-center justify-center">
                  <Clock className="h-7 w-7 md:h-8 md:w-8 text-secondary" />
                </div>
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary mb-3">
                  Klar på 2 minutter
                </h3>
                <p className="text-muted-foreground text-sm md:text-base">
                  Ingen blank side, ingen skrivekrampe. Udfyld formularen med dine historier — vi klarer resten. Du ville bruge timer selv. Vores AI er trænet specifikt til danske festtaler.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white hover-lift border-border/40" data-testid="why-card-2">
              <CardContent className="p-6 md:p-8 text-center">
                <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-5 md:mb-6 rounded-full bg-secondary/10 flex items-center justify-center">
                  <Target className="h-7 w-7 md:h-8 md:w-8 text-secondary" />
                </div>
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary mb-3">
                  Personlig — ikke generisk
                </h3>
                <p className="text-muted-foreground text-sm md:text-base">
                  Vores formular guider dig til at dele de helt rigtige detaljer: anekdoter, personlighed, humor. Resultatet er en tale der lyder som DIG — ikke som en skabelon fra nettet.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white hover-lift border-border/40" data-testid="why-card-3">
              <CardContent className="p-6 md:p-8 text-center">
                <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-5 md:mb-6 rounded-full bg-secondary/10 flex items-center justify-center">
                  <Pencil className="h-7 w-7 md:h-8 md:w-8 text-secondary" />
                </div>
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary mb-3">
                  Dit udgangspunkt — din tale
                </h3>
                <p className="text-muted-foreground text-sm md:text-base">
                  Du får 2 unikke udkast af din tale. Vælg din favorit, eller mix og match det bedste fra begge. Tilpas frit, tilføj dine egne ord, og gør den til din egen.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Price Anchoring */}
          <div className="mt-10 md:mt-12 text-center">
            <div className="inline-flex items-center gap-2 bg-secondary/10 px-6 py-3 rounded-full">
              <span className="text-muted-foreground text-sm md:text-base">En professionel taleskriver:</span>
              <span className="text-muted-foreground font-medium line-through">3.500 kr</span>
              <span className="text-primary mx-2">→</span>
              <span className="text-secondary font-bold text-lg">MinFesttale: fra 149 kr</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Guarantee Section */}
      <section className="py-12 md:py-16 bg-secondary/10 border-y border-secondary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6 md:gap-8 text-center">
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-full bg-secondary/20 flex items-center justify-center mb-4">
                <Eye className="h-7 w-7 text-secondary" />
              </div>
              <h3 className="font-heading text-lg font-bold text-primary mb-2">Se før du betaler</h3>
              <p className="text-muted-foreground text-sm">
                Du ser et preview med dine egne navne og anekdoter inden du betaler
              </p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-full bg-secondary/20 flex items-center justify-center mb-4">
                <Edit3 className="h-7 w-7 text-secondary" />
              </div>
              <h3 className="font-heading text-lg font-bold text-primary mb-2">Tilpas teksten selv</h3>
              <p className="text-muted-foreground text-sm">
                Efter køb kan du redigere og tilpasse talen til den er perfekt
              </p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-full bg-secondary/20 flex items-center justify-center mb-4">
                <RefreshCw className="h-7 w-7 text-secondary" />
              </div>
              <h3 className="font-heading text-lg font-bold text-primary mb-2">100% garanti</h3>
              <p className="text-muted-foreground text-sm">
                Ikke tilfreds? Generér en ny version gratis inden for 24 timer
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-12 md:py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-12">
            <span className="text-secondary font-medium text-sm uppercase tracking-wider">Vores produkter</span>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-bold text-primary mt-2">
              Vælg hvad du har brug for
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {/* Speech Card */}
            <Card className="bg-white hover-lift border-border/40 overflow-hidden" data-testid="product-tale">
              <div className="h-40 md:h-48 relative overflow-hidden bg-gradient-to-br from-primary/10 to-primary/5">
                <img
                  src="/brand/moods/06-skaal.png"
                  alt="Skål til festtale — bryllup, konfirmation, fødselsdag"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white/40 via-transparent to-transparent" />
              </div>
              <CardContent className="p-5 md:p-6">
                <h3 className="font-heading text-xl md:text-2xl font-bold text-primary mb-2">Festtale</h3>
                <p className="text-muted-foreground text-sm md:text-base mb-4">
                  Personlig tale til bryllup, konfirmation, fødselsdag eller anden anledning.
                </p>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-2xl md:text-3xl font-bold text-primary">Fra 149 kr</span>
                </div>
                <Link to="/tale">
                  <Button className="w-full bg-primary hover:bg-primary/90 rounded-full" data-testid="product-tale-cta">
                    Skriv en tale <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 md:py-20 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-12">
            <span className="text-secondary font-medium text-sm uppercase tracking-wider">Hvad siger andre</span>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-bold mt-2">
              Glade kunder
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Review 1 - 5 stars */}
            <Card 
              className="bg-primary-foreground/10 border-primary-foreground/20"
              data-testid="testimonial-1"
            >
              <CardContent className="p-5 md:p-6">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 md:h-5 md:w-5 fill-secondary text-secondary" />
                  ))}
                </div>
                <p className="text-primary-foreground/90 mb-4 italic text-sm md:text-base">
                  "Jeg sad i panik søndag aften inden min søsters bryllup og havde stadig ikke skrevet min tale. 20 minutter senere havde jeg en tale der fik hele bordet til at grine OG min mor til at græde. Jeg tilpassede et par formuleringer selv, men 90% var spot on fra starten."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                    <Users className="h-5 w-5 text-secondary" />
                  </div>
                  <div>
                    <p className="font-medium text-sm md:text-base">Mikkel, 34 år</p>
                    <p className="text-xs md:text-sm text-primary-foreground/60">Storebror · Bryllup</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Review 2 - 4 stars */}
            <Card 
              className="bg-primary-foreground/10 border-primary-foreground/20"
              data-testid="testimonial-2"
            >
              <CardContent className="p-5 md:p-6">
                <div className="flex mb-4">
                  {[...Array(4)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 md:h-5 md:w-5 fill-secondary text-secondary" />
                  ))}
                  <Star className="h-4 w-4 md:h-5 md:w-5 text-secondary/30" />
                </div>
                <p className="text-primary-foreground/90 mb-4 italic text-sm md:text-base">
                  "Talen til min søns konfirmation var rigtig god. Jeg ændrede lidt i afslutningen så den passede mere til vores familie, men alle anekdoterne sad lige i skabet. Fedt at man kan redigere bagefter."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                    <Users className="h-5 w-5 text-secondary" />
                  </div>
                  <div>
                    <p className="font-medium text-sm md:text-base">Helle, 47 år</p>
                    <p className="text-xs md:text-sm text-primary-foreground/60">Mor · Konfirmation</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Review 3 - 5 stars */}
            <Card 
              className="bg-primary-foreground/10 border-primary-foreground/20"
              data-testid="testimonial-3"
            >
              <CardContent className="p-5 md:p-6">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 md:h-5 md:w-5 fill-secondary text-secondary" />
                  ))}
                </div>
                <p className="text-primary-foreground/90 mb-4 italic text-sm md:text-base">
                  "Min kone fyldte 60 og jeg er IKKE typen der holder taler. Normalt ville jeg bare sige 'tillykke' og sætte mig ned. Talen var personlig, sjov og rørende — mine døtre troede jeg havde brugt uger på den."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                    <Users className="h-5 w-5 text-secondary" />
                  </div>
                  <div>
                    <p className="font-medium text-sm md:text-base">Per, 62 år</p>
                    <p className="text-xs md:text-sm text-primary-foreground/60">Mand · 60 års fødselsdag</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-12">
            <span className="text-secondary font-medium text-sm uppercase tracking-wider">Spørgsmål & svar</span>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-bold text-primary mt-2">
              Ofte stillede spørgsmål
            </h2>
          </div>

          <Accordion type="single" collapsible className="w-full space-y-3">
            <AccordionItem value="item-1" className="bg-white rounded-lg border border-border/40 px-6">
              <AccordionTrigger className="text-left font-medium text-primary hover:no-underline" data-testid="faq-1">
                Hvordan virker det?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Du udfylder en kort formular med information om personen og anledningen — navne, jeres relation, sjove anekdoter og hvad du gerne vil sige. Vores AI skriver en personlig tale baseret på dine input. Du kan se et preview med det samme, og efter betaling kan du redigere og tilpasse talen frit.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="bg-white rounded-lg border border-border/40 px-6">
              <AccordionTrigger className="text-left font-medium text-primary hover:no-underline" data-testid="faq-2">
                Er talen virkelig personlig?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Ja! Talen bygger 100% på de historier og detaljer du deler i formularen. Jo mere du skriver, jo bedre bliver talen. Vores AI opfinder aldrig historier — den bruger kun det du giver den.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="bg-white rounded-lg border border-border/40 px-6">
              <AccordionTrigger className="text-left font-medium text-primary hover:no-underline" data-testid="faq-3">
                Kan jeg redigere talen bagefter?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Absolut. Efter køb får du adgang til en editor hvor du kan ændre formuleringer, tilføje sætninger og gøre talen helt til din egen. Du kan downloade den færdige tale som PDF og ren tekst.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="bg-white rounded-lg border border-border/40 px-6">
              <AccordionTrigger className="text-left font-medium text-primary hover:no-underline" data-testid="faq-4">
                Hvad hvis jeg ikke er tilfreds?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Du har 100% tilfredshedsgaranti. Inden for 24 timer efter køb kan du få genereret en helt ny version gratis. Du kan også prøve ubegrænset med forskellige toner inden du betaler.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="bg-white rounded-lg border border-border/40 px-6">
              <AccordionTrigger className="text-left font-medium text-primary hover:no-underline" data-testid="faq-5">
                Er det AI der skriver talen?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Ja, vi bruger AI-teknologi der er specialiseret i danske festtaler. Men tænk på det som et samarbejde: Du leverer historierne og det personlige — vores AI pakker det ind i en velskrevet tale med den rigtige opbygning, timing og tone. Du tilpasser den til den er perfekt.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="bg-white rounded-lg border border-border/40 px-6">
              <AccordionTrigger className="text-left font-medium text-primary hover:no-underline" data-testid="faq-6">
                Hvor lang tid tager det?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Selve genereringen tager under 30 sekunder. Med formularen medregnet er de fleste færdige på 2-5 minutter. Redigering bagefter er op til dig.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7" className="bg-white rounded-lg border border-border/40 px-6">
              <AccordionTrigger className="text-left font-medium text-primary hover:no-underline" data-testid="faq-7">
                Hvad er forskellen på kort, medium og lang tale?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Kort (~2 min taletid, 350-450 ord) er perfekt hvis du vil sige noget kort og rammende. Medium (~4 min, 700-900 ord) giver plads til 2-3 anekdoter og er vores mest populære. Lang (~6 min, 1000-1300 ord) er til den store lejlighed hvor du virkelig vil udfolde historien.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-8" className="bg-white rounded-lg border border-border/40 px-6">
              <AccordionTrigger className="text-left font-medium text-primary hover:no-underline" data-testid="faq-8">
                Kan jeg se et eksempel inden jeg starter?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Ja! Længere oppe på denne side kan du se tre komplette eksempler — en bryllupstale, en konfirmationstale og en fødselsdagstale — med den formular der blev udfyldt og den færdige tale. Du kan også se et preview af din egen tale inden du betaler.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-9" className="bg-white rounded-lg border border-border/40 px-6">
              <AccordionTrigger className="text-left font-medium text-primary hover:no-underline" data-testid="faq-9">
                Bliver mine personlige oplysninger gemt?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Vi tager dit privatliv alvorligt. Dine formulardata bruges kun til at generere din tale og slettes automatisk efter 30 dage. Vi sælger aldrig dine data, og de bruges ikke til at træne AI-modeller. Læs mere i vores <Link to="/privatlivspolitik" className="text-secondary underline hover:text-secondary/80">privatlivspolitik</Link>.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-10" className="bg-white rounded-lg border border-border/40 px-6">
              <AccordionTrigger className="text-left font-medium text-primary hover:no-underline" data-testid="faq-10">
                Kan jeg bruge talen til en anledning I ikke har på listen?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Ja! Udover bryllup, konfirmation og rund fødselsdag kan du vælge "Anden anledning" og beskrive festen. Vores AI tilpasser talen til alt fra studenterfest til sølvbryllup til afskedsfest.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-11" className="bg-white rounded-lg border border-border/40 px-6">
              <AccordionTrigger className="text-left font-medium text-primary hover:no-underline" data-testid="faq-11">
                Hvorfor ikke bare bruge ChatGPT?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Det kan du — men ChatGPT kender ikke danske taletraditioner, giver dig ingen talenoter med pauser og leveringstips, og det tager typisk 30+ minutter at prompte sig frem til et brugbart resultat. MinFesttale er bygget specifikt til danske festtaler: Du svarer på simple spørgsmål, får 2 unikke udkast med talenoter på 30 sekunder, og kan redigere og downloade som flot PDF. Det er forskellen på at bygge selv og at få det leveret.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-12" className="bg-white rounded-lg border border-border/40 px-6">
              <AccordionTrigger className="text-left font-medium text-primary hover:no-underline" data-testid="faq-12">
                Hvordan kontakter jeg kundeservice?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Skriv til <a href="mailto:hej@minfesttale.dk" className="text-secondary underline">hej@minfesttale.dk</a> — vi svarer typisk inden for 24 timer (ofte hurtigere). Vi tilbyder kun kundeservice via e-mail, ikke telefon, så vi kan holde priserne lave og bruge tiden på at hjælpe dig med din tale i stedet.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Blog Teaser Section */}
      <section className="py-12 md:py-16 bg-background" data-testid="blog-teaser-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="text-secondary font-medium text-sm uppercase tracking-wider">Fra bloggen</span>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary mt-2">
              Guides til den perfekte tale
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { id: "bryllupstale-guide", title: "Sådan skriver du den perfekte bryllupstale", cat: "Bryllup", color: "bg-rose-500" },
              { id: "konfirmationstale-guide", title: "Konfirmationstale: Guide til forældre", cat: "Konfirmation", color: "bg-amber-500" },
              { id: "foedselsdagstale-tips", title: "Fødselsdagstale til rund fødselsdag", cat: "Fødselsdag", color: "bg-emerald-500" },
            ].map((post) => (
              <Link key={post.id} to={`/blog/${post.id}`}>
                <Card className="bg-white hover-lift border-border/40 cursor-pointer h-full" data-testid={`blog-teaser-${post.id}`}>
                  <CardContent className="p-5">
                    <div className={`w-8 h-8 rounded-full ${post.color} flex items-center justify-center mb-3`}>
                      <FileText className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{post.cat}</span>
                    <h3 className="font-heading text-base font-bold text-primary mt-1">{post.title}</h3>
                    <span className="inline-flex items-center gap-1 text-secondary text-sm font-medium mt-2">
                      Læs mere <ChevronRight className="h-3 w-3" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link to="/blog" className="text-secondary font-medium hover:underline text-sm" data-testid="blog-see-all">
              Se alle blogindlæg <ChevronRight className="inline h-3 w-3" />
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-12 md:py-16 bg-primary" data-testid="newsletter-section">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Mail className="h-10 w-10 text-secondary mx-auto mb-4" />
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-3">
            Få tips til din festtale
          </h2>
          <p className="text-white/70 mb-6">
            Tilmeld dig vores nyhedsbrev og få gode råd til at skrive og holde den perfekte tale.
          </p>
          <NewsletterForm />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart className="h-10 w-10 md:h-12 md:w-12 text-secondary mx-auto mb-6" />
          <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-bold text-primary mb-4">
            Klar til at skrive din tale?
          </h2>
          <p className="text-base md:text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
            Det tager kun 2 minutter, og du vil elske resultatet.
            Du ser preview først, kan tilpasse teksten selv, og har 100% garanti.
          </p>
          
          {/* Trust reminder */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
              <Shield className="h-4 w-4 text-accent" />
              100% tilfredshedsgaranti
            </span>
            <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
              <Edit3 className="h-4 w-4 text-accent" />
              Tilpas selv teksten
            </span>
            <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
              <Eye className="h-4 w-4 text-accent" />
              Se preview før køb
            </span>
          </div>

          <div className="flex justify-center">
            <Link to="/tale">
              <Button 
                size="lg" 
                className="bg-secondary hover:bg-secondary/90 text-primary rounded-full px-8 py-6 text-lg"
                data-testid="bottom-cta-tale"
              >
                <FileText className="mr-2 h-5 w-5" />
                Skriv en festtale
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
